import React, { useMemo } from 'react';
import { formatDate } from '../../utils/labels';
import { formatLineCrosserRankCopy } from '../../utils/rankingsAnalytics';
import CountryFlag from './CountryFlag';
import InfoHint from './InfoHint';
import PlayerCell from './PlayerCell';
import './BubbleWatchRail.css';

const QUALIFICATION_CUTOFF = 60;

const rankLabel = (rank) => `Rank ${rank ?? '—'}`;

const lineStatusLabel = (status) => {
  if (status === 'on') return 'On the line';
  if (status === 'inside') return 'Inside the line';
  return 'First outside';
};

const deltaLabel = (delta) => {
  if (!delta) return null;
  return delta > 0 ? `+${delta}` : `${delta}`;
};

const RankSparkline = ({ points = [], tone = 'green', compact = false }) => {
  if (!points.length) return null;

  const width = 180;
  const height = compact ? 28 : 44;
  const ranks = points.map((point) => point.olympicRank);
  const minRank = Math.min(...ranks, QUALIFICATION_CUTOFF);
  const maxRank = Math.max(...ranks, QUALIFICATION_CUTOFF);
  const range = Math.max(1, maxRank - minRank);
  const xStep = points.length > 1 ? width / (points.length - 1) : width;
  const yForRank = (rank) => 5 + ((rank - minRank) / range) * (height - 10);
  const linePoints = points
    .map((point, idx) => `${idx * xStep},${yForRank(point.olympicRank)}`)
    .join(' ');
  const cutoffY = yForRank(QUALIFICATION_CUTOFF);

  return (
    <div className={`bw-sparkline bw-sparkline--${tone} ${compact ? 'bw-sparkline--compact' : ''}`}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden>
        <line x1="0" x2={width} y1={cutoffY} y2={cutoffY} className="bw-sparkline-cutoff" />
        <polyline points={linePoints} className="bw-sparkline-line" />
        {points.map((point, idx) => (
          <circle
            key={`${point.label}-${point.olympicRank}`}
            cx={idx * xStep}
            cy={yForRank(point.olympicRank)}
            r={compact ? 2.6 : 3.2}
            className="bw-sparkline-dot"
          />
        ))}
      </svg>
      {!compact && (
        <div className="bw-sparkline-labels">
          {points.map((point) => (
            <span key={`${point.label}-${point.olympicRank}`}>
              {point.label}: {point.olympicRank}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CutLineRow = ({ row, onPlayerClick }) => {
  const { entry, history, lineStatus, delta } = row;
  const isOutside = entry.olympicRank > QUALIFICATION_CUTOFF;

  return (
    <button
      type="button"
      className={`bubble-card bubble-card--${lineStatus}`}
      onClick={() => onPlayerClick(entry)}
    >
      <span className="bubble-card-main">
      <PlayerCell
        firstName={entry.firstName}
        lastName={entry.lastName}
        country={entry.country}
        headshotUrl={entry.headshotUrl}
      />
        <span className="bubble-card-meta">
          {rankLabel(entry.olympicRank)} · {lineStatusLabel(lineStatus)}
        </span>
      </span>
      <span className="bubble-card-country">
        <CountryFlag code={entry.country} size={16} />
        {entry.country}
      </span>
      {deltaLabel(delta) && (
        <span className={`bubble-card-delta ${delta > 0 ? 'move-up' : 'move-down'}`}>
          {deltaLabel(delta)}
        </span>
      )}
      <RankSparkline points={history} tone={isOutside ? 'gold' : 'green'} compact />
    </button>
  );
};

const LineCrosserRow = ({ mover, onPlayerClick }) => {
  const movedIn = mover.crossingType === 'in';

  return (
    <button
      type="button"
      className={`line-crosser-row ${movedIn ? 'line-crosser-row--in' : 'line-crosser-row--out'}`}
      onClick={() => onPlayerClick(mover.entry)}
    >
      <span className={`line-crosser-badge ${movedIn ? 'move-up' : 'move-down'}`}>
        {movedIn ? 'IN' : 'OUT'}
      </span>
      <span className="line-crosser-copy">
        <PlayerCell
          firstName={mover.entry.firstName}
          lastName={mover.entry.lastName}
          country={mover.entry.country}
          headshotUrl={mover.entry.headshotUrl}
        />
        <small>{formatLineCrosserRankCopy(mover)}</small>
      </span>
      <span className="line-crosser-country">
        <CountryFlag code={mover.entry.country} size={16} />
        {mover.entry.country}
      </span>
    </button>
  );
};

const BubbleWatchRail = ({
  cutLineWatch = [],
  gender,
  lineCrossers = [],
  list,
  showExtendedRanks = false,
  onPlayerClick,
}) => {
  const lineRows = useMemo(() => cutLineWatch, [cutLineWatch]);
  const snapshotDate = list?.snapshotDate || list?.updatedAt || list?.createdAt;
  const titleGender = gender === 'women' ? "Women's" : "Men's";

  return (
    <aside className="bubble-watch-rail" aria-label="Qualification watch">
      <header className="bubble-watch-header">
        <p>ROAD TO LA</p>
        <span>
          {titleGender} field | {snapshotDate ? formatDate(snapshotDate) : 'Latest snapshot'}
        </span>
      </header>

      <section className="bubble-watch-section">
        <div className="bubble-section-title bubble-section-title--crossers">
          <span className="bubble-section-title-main">
            <span>Moved In / Moved Out</span>
            <InfoHint label="Moved In / Moved Out">
              Athletes who entered or exited the top 60 OGR since the previous published ranking.
            </InfoHint>
          </span>
        </div>
        <div className="line-crosser-list">
          {lineCrossers.length ? (
            lineCrossers.map((mover) => (
              <LineCrosserRow key={mover.key} mover={mover} onPlayerClick={onPlayerClick} />
            ))
          ) : (
            <p className="bubble-watch-empty">No one crossed the qualification line between the last two lists.</p>
          )}
        </div>
      </section>

      <section className="bubble-watch-section">
        <div className="bubble-section-title">
          <span className="bubble-section-title-main">
            <span>Current Qualifier Watch</span>
            <InfoHint label="Current Qualifier Watch">
              {showExtendedRanks
                ? 'Athletes closest to the top-60 Olympic qualification line, both inside and just outside.'
                : 'Athletes currently ranked 55–60, closest to the Olympic qualification line.'}
            </InfoHint>
          </span>
          <small>{showExtendedRanks ? 'Ranks 55-65 around the line' : 'Ranks 55-60 inside the line'}</small>
        </div>
        <div className="bubble-card-list">
          {lineRows.map((row) => (
            <React.Fragment key={row.key}>
              {row.endRank === QUALIFICATION_CUTOFF + 1 && (
                <div className="cut-line-marker">
                  <span />
                  <strong>Qualification line</strong>
                  <span />
                </div>
              )}
              <CutLineRow row={row} onPlayerClick={onPlayerClick} />
            </React.Fragment>
          ))}
        </div>
      </section>
    </aside>
  );
};

export default BubbleWatchRail;
